"use client";

import Link from "next/link";
import Image from "next/image";
import { getBillingCycle } from "@litonarefin/utils";
import Coupon from "./Coupon";

const ProductDetails = ({ products, paddle }) => {
  return (
    <div className="jlt-py-7 md:jlt-py-12 jlt-px-4 md:jlt-px-8 jlt-rounded-r-lg jlt-bg-[#FCFDFF]">
      <h2 className="jlt-text-xl jlt-text-[#16213E] jlt-font-bold">Your Order</h2>

      <div className="jlt-mt-4">
        {products?.items?.map((product, i) => (
          <div
            key={i}
            className="jlt-py-4 jlt-w-full jlt-h-[74px] jlt-flex jlt-justify-between jlt-items-center"
          >
            <div className="jlt-flex jlt-items-center jlt-gap-5">
              <div className="jlt-relative">
                <img src={product.product.image_url} alt={"Product Image"} width={40} height={40} />
                <span className="jlt-absolute -jlt-top-[6px] -jlt-right-[6px] jlt-bg-[var(--primary)] jlt-w-4 jlt-h-4 jlt-rounded-full jlt-flex jlt-items-center jlt-justify-center jlt-text-[11px] jlt-text-white jlt-font-bold jlt-box-shadow-[0px_0px_16px_rgba(16,24,40,0.02)]">
                  {product.quantity}
                </span>
              </div>
              <div>
                <h3 className="jlt-text-base jlt-text-[#16213E] jlt-font-semibold">
                  {product.product.name}
                </h3>
                <span className="jlt-text-xs jlt-text-[#72798A] jlt-font-semibold">
                  License: {product?.price_name?.split("-")?.[1] || product?.price_name}
                </span>
              </div>
            </div>
            <h3 className="jlt-text-md lg:jlt-text-lg jlt-text-[#16213E] jlt-font-semibold">
              ${product.totals.subtotal.toFixed(2)}
              <span className="jlt-text-xs jlt-text-[#72798A] jlt-font-normal">
                / {getBillingCycle(product?.billing_cycle)}
              </span>
            </h3>
          </div>
        ))}

        <hr />

        <div className="jlt-py-2 jlt-flex jlt-justify-between jlt-items-center">
          <h3 className="jlt-text-sm jlt-text-[#72798A] jlt-font-semibold">Subtotal</h3>
          <h3 className="jlt-text-sm jlt-text-[#3F475A] jlt-font-semibold">
            ${products?.totals?.subtotal.toFixed(2)}
          </h3>
        </div>

        <div className="jlt-py-2 jlt-flex jlt-justify-between jlt-items-center">
          <h3 className="jlt-text-sm jlt-text-[#72798A] jlt-font-semibold">Discount</h3>
          <h3 className="jlt-text-sm jlt-text-[#3F475A] jlt-font-semibold">
            - ${products?.totals?.discount.toFixed(2)}
          </h3>
        </div>

        <div className="jlt-py-2 jlt-flex jlt-justify-between jlt-items-center">
          <h3 className="jlt-text-sm jlt-text-[#72798A] jlt-font-semibold">Tax</h3>
          <h3 className="jlt-text-sm jlt-text-[#3F475A] jlt-font-semibold">
            ${products?.totals?.tax.toFixed(2)}
          </h3>
        </div>

        <div className="jlt-py-2 jlt-flex jlt-justify-between jlt-items-center jlt-border-t-[1px] jlt-border-[#EBEEF5]">
          <h3 className="jlt-text-lg jlt-text-[#16213E] jlt-font-semibold">Total</h3>
          <h3 className="jlt-text-lg jlt-text-[var(--primary)] jlt-font-semibold">
            ${products?.totals?.total.toFixed(2)}
          </h3>
        </div>

        <Coupon paddle={paddle} items={products?.items} />
      </div>

      <div className="jlt-py-6">
        <h2 className="jlt-text-base jlt-text-[#16213E] jlt-font-bold">
          Supported Payment Methods
        </h2>
        <div className="jlt-mt-6 jlt-flex jlt-gap-1">
          <Image
            src={"/supported-payments.svg"}
            alt="Supported Payments"
            width={236}
            height={24}
            sizes="100vw"
          />
        </div>
        <p className="jlt-mt-[6px] jlt-text-xs jlt-text-[#3F475A] jlt-font-normal">
          Pay with Credit Card, PayPal, Amazon Pay and more.
        </p>
      </div>

      <div className="jlt-py-4 jlt-mt-6">
        <p className="jlt-text-sm jlt-text-[#3F475A] jlt-font-normal">
          <span>By checking out, you agree to our</span>{" "}
          <Link
            prefetch={false}
            href="/terms-of-use"
            className="jlt-text-[var(--primary)] jlt-underline jlt-underline-offset-1"
          >
            terms and conditions
          </Link>{" "}
          <span>and</span>{" "}
          <Link
            prefetch={false}
            href="/privacy-policy"
            className="jlt-text-[var(--primary)] jlt-underline jlt-underline-offset-1"
          >
            privacy policy.
          </Link>{" "}
          <span>
            You also authorize your payment method to be saved and charged on a recurring basis
            until you cancel, which you can do at any time.
          </span>
        </p>
      </div>

      <div className="jlt-mt-4 jlt-pt-4 jlt-pb-4">
        <div className="jlt-flex jlt-flex-col jlt-items-center">
          <Image src={"/badge.png"} alt={"money-back"} width={180} height={128} sizes="100vw" />
        </div>
        <h3 className="jlt-mt-4 jlt-text-lg jlt-text-[#3F475A] jlt-font-bold jlt-text-center">
          Money Back Guarantee
        </h3>
        <p className="jlt-mt-2 jlt-text-sm jlt-text-[#3F475A] jlt-font-normal jlt-text-center">
          We offer a 14 days money back guarantee if you are unhappy with our products or service.
          Get a full refund, no hassle, no stress.
        </p>
      </div>

      <hr />

      <div className="jlt-py-4 jlt-flex jlt-gap-[10px] jlt-justify-center">
        <Image src={"/secure-image.svg"} alt="Secure Image" width={194} height={32} sizes="100vw" />
      </div>
    </div>
  );
};

export default ProductDetails;
